// Decrypts the /api/status/lookup response client-side (STA-03, CRY-04).
//
// Anonymity rationale:
//   - The Worker stores ciphertext + wrapped DEK only. Decrypt happens entirely
//     in the browser using KEK_protocolo derived from Argon2id(protocolo+passphrase).
//   - The denunciante never re-sends cleartext credentials to the server: the
//     status page derives the same 64 bytes via Argon2id, splits authHash[0..32]
//     for lookup, and uses kekRaw[32..64] to unwrap the DEK locally.
//   - Each MockMessage / InfoComplementar uses a fresh 12-byte AES-GCM IV but
//     the same DEK (D-32). AAD=caseId binds every ciphertext to the case.

import { unwrapDek, decryptPayload, messageAad } from '@aions/crypto';
import { base64ToBytes } from '../utils/bytes';

/**
 * MockMessage origin — must stay in sync with packages/core Message.origin
 * union. The denunciante decrypts every message with the same DEK; `origin`
 * is plaintext metadata used by the SPA to choose visual treatment (cor +
 * título) E a AAD (Div3 vs legado). No PII — only one of a finite set of enums.
 */
export type MessageOrigin =
  | 'comite_message'
  | 'denunciante_message'
  | 'ia_analista_parecer'
  | 'ia_juiz_sintese'
  | 'official_decisao'
  | 'recurso_decidido';

/**
 * Divergência 3 — AAD por origin. Os canais bidirecionais Div3 (comite_message /
 * denunciante_message) amarram caseId+ulid via `messageAad` (a MESMA função que o
 * comitê usa para cifrar, @aions/crypto). As notificações legadas (recurso_decidido,
 * official_decisao, IA, ou sem origin) seguem AAD=caseId (contrato histórico).
 */
const DIV3_ORIGINS: ReadonlySet<MessageOrigin> = new Set(['comite_message', 'denunciante_message']);
function aadForMessage(caseId: string, ulid: string, origin?: MessageOrigin): string {
  return origin !== undefined && DIV3_ORIGINS.has(origin) ? messageAad(caseId, ulid) : caseId;
}

/** Wire shape of the /api/status/lookup response — must match apps/api/src/routes/status. */
export interface StatusLookupResponse {
  caseId: string;
  ciphertext: string;
  nonce: string;
  dekWrappedByProtocolo: string;
  state: { kind: string };
  unseenPhaseChange: boolean;
  mockMessages: ReadonlyArray<{
    ulid: string;
    ciphertext: string;
    nonce: string;
    createdWeek: string;
    /** Só nas mensagens de ator identificado (comitê/painel/IA) — ver
     *  `selarMensagem` no core. Ausente nas do denunciante, por anonimato. */
    createdAtEpochMs?: number;
    origin?: MessageOrigin;
  }>;
  infoComplementar: ReadonlyArray<{
    ulid: string;
    ciphertext: string;
    nonce: string;
    createdWeek: string;
  }>;
  attachments: ReadonlyArray<{ contentHash: string; nonce: string; sizeBucket: string }>;
  /** Divergência 3 — canal de mensagens ligado (server). Ausente em respostas
   *  legadas (tratado como false → composers inertes). */
  messagingEnabled?: boolean;
}

export interface DecodedDenunciaPayload {
  descricao: string;
  quando: string;
  onde: string;
  envolvidos: string;
  identificacaoVoluntaria: boolean;
}

export interface DecodedMessage {
  ulid: string;
  plaintext: string;
  createdWeek: string;
  /** Data e hora do envio, só para mensagens de ator identificado (comitê). Tem
   *  de ser repassado EXPLICITAMENTE no map abaixo: esta projeção monta o objeto
   *  campo por campo, e campo não copiado desaparece sem erro — é o modo de falha
   *  do `keypairRef` (§"fallback plausível esconde ausência"). */
  createdAtEpochMs?: number;
  origin?: MessageOrigin;
  /**
   * D4 — o item EXISTE e não pôde ser aberto (docs/LEVANTAMENTO-CHAT-2026-07-28.md §D4).
   *
   * Antes, falha de decifra virava `plaintext: ''` e a tela filtrava por `plaintext`:
   * o item sumia e NADA indicava que ele existia. Medido em 29/07 contra o worker
   * real: servidor entregou 9 mensagens, a tela mostrou 6, escondeu 3 em silêncio.
   * O comitê, no mesmo caso, mostra marcador — a assimetria deixava só o denunciante
   * no escuro, que é o lado que menos pode ficar.
   *
   * A lib NÃO fabrica texto de erro: marca o item e devolve `plaintext: ''`. A copy
   * é da tela (i18n) — a lib não conhece idioma.
   */
  falhaDecifra?: true;
}

/**
 * Visual category for a decrypted MockMessage — drives badge color / title in
 * StatusPage. Anonymity-safe: maps only to a finite enum + an i18n key. The
 * `accent` slug is appended to a CSS class (`.message-accent-{accent}`) and
 * also used as a stable selector for tests.
 *
 * - `recurso` (purple) — REC-04 notificação de decisão de recurso
 * - `decisao` (green) — decisão oficial de 1ª instância
 * - `info` (blue) — qualquer outra mensagem do comitê / IA
 */
export interface MessageCategory {
  readonly label: string; // i18n key
  readonly accent: 'info' | 'decisao' | 'recurso';
}

/**
 * Pure mapping from MockMessage.origin to UI category. Default (when origin
 * is missing or `comite_message` / IA pareceres) → `info`. REC-04 explicitly
 * elevates `recurso_decidido` to a distinct accent so the denunciante notices
 * the decisão without needing to read the message body first.
 */
export function categorizeMessage(msg: { origin?: MessageOrigin }): MessageCategory {
  switch (msg.origin) {
    case 'recurso_decidido':
      return { label: 'status.decisao_recurso_titulo', accent: 'recurso' };
    case 'official_decisao':
      return { label: 'status.decisao_oficial_titulo', accent: 'decisao' };
    // Divergência 3 — resposta do PRÓPRIO denunciante, ecoada no thread.
    case 'denunciante_message':
      return { label: 'status.sua_resposta_titulo', accent: 'info' };
    case 'comite_message':
    case 'ia_analista_parecer':
    case 'ia_juiz_sintese':
    default:
      return { label: 'status.mensagem_comite_titulo', accent: 'info' };
  }
}

export interface DecryptedStatus {
  caseId: string;
  state: { kind: string };
  unseenPhaseChange: boolean;
  payload: DecodedDenunciaPayload;
  mockMessages: ReadonlyArray<DecodedMessage>;
  infoComplementar: ReadonlyArray<DecodedMessage>;
  attachments: StatusLookupResponse['attachments'];
  /** DEK retained so the Status page can re-encrypt info-complementar + respostas
   *  (denunciante_message) with the same key (D-32). Zerado pelo caller no unmount. */
  dekRaw: Uint8Array;
  /** Divergência 3 — canal de mensagens ligado (server) → mostra os composers. */
  messagingEnabled: boolean;
}

/**
 * Decrypts the lookup response. Throws if AAD/MAC fails (treated as not-found
 * by the caller, mirroring server-side enumeration mitigation D-31).
 */
export async function decryptStatusResponse(
  response: StatusLookupResponse,
  kekRaw: Uint8Array,
): Promise<DecryptedStatus> {
  const wrappedDek = base64ToBytes(response.dekWrappedByProtocolo);
  const dekRaw = await unwrapDek(wrappedDek, kekRaw);

  const ciphertext = base64ToBytes(response.ciphertext);
  const nonce = base64ToBytes(response.nonce);
  const json = await decryptPayload(ciphertext, nonce, dekRaw, response.caseId);
  const payload = JSON.parse(json) as DecodedDenunciaPayload;

  // Fix seed msg0 (bloco "Mensagem do Comitê" vazio): só mensagens com conteúdo
  // real (ciphertext+nonce não-vazios) viram item. O placeholder legado
  // (ciphertext:'') é descartado AQUI — no ponto onde o ciphertext ainda existe —
  // para nunca renderizar um bloco oco. StatusPage também filtra por plaintext
  // (belt-and-suspenders); esta é a defesa permanente na camada de dados.
  const realMessages = response.mockMessages.filter((m) => m.ciphertext !== '' && m.nonce !== '');
  const mockMessages = await Promise.all(
    realMessages.map(async (m): Promise<DecodedMessage> => {
      // AAD por origin (Div3 → caseId+ulid; legado → caseId). Falha por-item vira
      // plaintext '' — o StatusPage filtra por plaintext, então uma mensagem que
      // não decifra some em vez de derrubar TODO o lookup (que viraria "não
      // encontrado" — alarmante para o denunciante). O corpo da denúncia (acima)
      // segue lançando: aí sim é passphrase errada = not-found (D-31).
      let plaintext = '';
      let falhou = false;
      try {
        plaintext = await decryptPayload(
          base64ToBytes(m.ciphertext),
          base64ToBytes(m.nonce),
          dekRaw,
          aadForMessage(response.caseId, m.ulid, m.origin),
        );
      } catch {
        // D4 — o item NÃO some. Vira marcado, e a tela diz que existe algo que não
        // pôde ser lido. A alternativa antiga (sumir) fazia o denunciante tomar
        // decisão sobre uma conversa incompleta sem saber que estava incompleta.
        falhou = true;
      }
      // Spread origin conditionally — exactOptionalPropertyTypes rejects
      // explicit `origin: undefined` on an `origin?:` field.
      return {
        ulid: m.ulid,
        plaintext,
        createdWeek: m.createdWeek,
        // Condicional pelo mesmo motivo do `origin`: exactOptionalPropertyTypes
        // recusa `createdAtEpochMs: undefined` explícito num campo `?:`.
        ...(m.createdAtEpochMs !== undefined ? { createdAtEpochMs: m.createdAtEpochMs } : {}),
        ...(m.origin !== undefined ? { origin: m.origin } : {}),
        ...(falhou ? { falhaDecifra: true as const } : {}),
      };
    }),
  );

  // D4 (variante PIOR, achada ao consertar as mensagens) — este `Promise.all` NÃO
  // tinha try/catch por item. UM item que não abrisse rejeitava a promessa inteira,
  // `decryptStatusResponse` lançava, e o `StatusPage` caía no catch que mostra
  // `status.not_found`. Resultado: a denúncia INTEIRA sumia e o denunciante lia
  // "não encontrado" com o protocolo e a frase-senha CORRETOS — indistinguível de
  // "sua denúncia não existe". Agora é por item, igual às mensagens.
  const infoComplementar = await Promise.all(
    response.infoComplementar.map(async (m): Promise<DecodedMessage> => {
      try {
        const plaintext = await decryptPayload(
          base64ToBytes(m.ciphertext),
          base64ToBytes(m.nonce),
          dekRaw,
          response.caseId,
        );
        return { ulid: m.ulid, plaintext, createdWeek: m.createdWeek };
      } catch {
        return { ulid: m.ulid, plaintext: '', createdWeek: m.createdWeek, falhaDecifra: true };
      }
    }),
  );

  return {
    caseId: response.caseId,
    state: response.state,
    unseenPhaseChange: response.unseenPhaseChange,
    payload,
    mockMessages,
    infoComplementar,
    attachments: response.attachments,
    dekRaw,
    messagingEnabled: response.messagingEnabled ?? false,
  };
}
